import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Upload,
  Trash2,
  Copy,
  FolderOpen,
  Image as ImageIcon,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  X,
  ZoomIn,
  ZoomOut,
  Download,
  Loader2,
  Calendar,
  Phone,
  Filter,
  Grid,
  List,
  MessageSquare,
  Check,
  Save,
  CheckSquare,
  Square,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Sidebar from "@/components/Sidebar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ImageDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderImages, setFolderImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [stats, setStats] = useState({});
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "list"
  const [expandedDates, setExpandedDates] = useState({});
  
  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxZoom, setLightboxZoom] = useState(1);
  
  // Caption editing state
  const [editingCaption, setEditingCaption] = useState(null);
  const [captionText, setCaptionText] = useState("");
  const [savingCaption, setSavingCaption] = useState(false);
  const [folderOrderId, setFolderOrderId] = useState(null);
  
  // Bulk selection state
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  // Lightbox navigation
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxZoom(1);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxZoom(1);
  };

  const goToPrevImage = () => {
    setLightboxIndex((prev) => (prev > 0 ? prev - 1 : folderImages.length - 1));
    setLightboxZoom(1);
  };

  const goToNextImage = () => {
    setLightboxIndex((prev) => (prev < folderImages.length - 1 ? prev + 1 : 0));
    setLightboxZoom(1);
  };

  // Handle folder or search query parameter - open specific folder
  useEffect(() => {
    if (folders.length === 0) return;
    
    const folderParam = searchParams.get('folder');
    const searchParam = searchParams.get('search');
    
    if (folderParam) {
      const targetFolder = folders.find(f => f.path === folderParam);
      if (targetFolder) {
        fetchFolderImages(targetFolder);
        toast.success(`Opened folder: ${targetFolder.phone_number}`);
        setSearchParams({});
      }
    } else if (searchParam) {
      // Search by phone number - find first matching folder
      const targetFolder = folders.find(f => f.phone_number.includes(searchParam));
      if (targetFolder) {
        fetchFolderImages(targetFolder);
        toast.success(`Found folder for: ${targetFolder.phone_number}`);
        setSearchParams({});
      } else {
        // Set search query in the search box
        setSearchQuery(searchParam);
        setSearchParams({});
      }
    }
  }, [folders, searchParams]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === "ArrowLeft") goToPrevImage();
      if (e.key === "ArrowRight") goToNextImage();
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, folderImages.length]);

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    if (!selectedFolder) {
      toast.error("Please select a folder first");
      return;
    }

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await uploadMultipleFiles(files);
    }
  };

  // File input ref for direct upload
  const fileInputRef = useRef(null);

  const uploadMultipleFiles = async (files) => {
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/heic", "image/heif", "image/bmp", "image/tiff"];
    const validFiles = Array.from(files).filter(file => validTypes.includes(file.type));

    if (validFiles.length === 0) {
      toast.error("Please select valid image files (JPG, PNG, GIF, WEBP)");
      return;
    }

    if (!selectedFolder) {
      toast.error("Please select a folder first");
      return;
    }

    setUploadingFile(true);
    toast.info(`Uploading ${validFiles.length} image${validFiles.length > 1 ? 's' : ''}...`);

    // Upload files in parallel (max 5 at a time for better performance)
    const batchSize = 5;
    let successCount = 0;
    let errorCount = 0;

    // Encode the folder path properly for the URL
    const folderPath = encodeURIComponent(selectedFolder.path).replace(/%2F/g, '/');

    for (let i = 0; i < validFiles.length; i += batchSize) {
      const batch = validFiles.slice(i, i + batchSize);
      const promises = batch.map(async (file) => {
        try {
          const formData = new FormData();
          formData.append("file", file);

          await axios.post(`${API}/images/folder/${folderPath}/upload`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          return { success: true };
        } catch (error) {
          console.error("Upload error:", error);
          return { success: false };
        }
      });

      const results = await Promise.all(promises);
      results.forEach(r => {
        if (r.success) successCount++;
        else errorCount++;
      });
    }

    setUploadingFile(false);

    if (successCount > 0) {
      toast.success(`${successCount} image${successCount > 1 ? 's' : ''} uploaded successfully`);
      fetchFolderImages(selectedFolder);
      fetchFolders();
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} image${errorCount > 1 ? 's' : ''} failed to upload`);
    }
  };

  // Handle file input change (direct upload without popup)
  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      uploadMultipleFiles(files);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  // Open file picker directly
  const openFilePicker = () => {
    if (!selectedFolder) {
      toast.error("Please select a folder first");
      return;
    }
    fileInputRef.current?.click();
  };


  useEffect(() => {
    fetchFolders();
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/images/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchFolders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/images/folders`);
      setFolders(response.data.folders || []);
    } catch (error) {
      console.error("Error fetching folders:", error);
      toast.error("Failed to load image folders");
    } finally {
      setLoading(false);
    }
  };

  const fetchFolderImages = async (folder) => {
    try {
      setLoadingImages(true);
      setSelectedFolder(folder);
      setEditingCaption(null);
      setCaptionText("");
      setSelectedImages([]);
      setSelectionMode(false);
      const response = await axios.get(`${API}/images/folder/${encodeURIComponent(folder.path)}`);
      setFolderImages(response.data.images || []);
      setFolderOrderId(response.data.order_id || null);
    } catch (error) {
      console.error("Error fetching folder images:", error);
      toast.error("Failed to load images");
    } finally {
      setLoadingImages(false);
    }
  };

  // Caption handling functions
  const handleStartEditCaption = (image) => {
    setEditingCaption(image.path);
    setCaptionText(image.caption || "");
  };

  const handleSaveCaption = async (imagePath) => {
    if (!selectedFolder) {
      toast.error("No folder selected");
      return;
    }
    try {
      setSavingCaption(true);
      const response = await axios.put(
        `${API}/images/folder/${encodeURIComponent(selectedFolder.path)}/caption`,
        { image_path: imagePath, caption: captionText }
      );
      if (response.data.success) {
        toast.success("Caption saved!");
        setEditingCaption(null);
        setCaptionText("");
        // Update local state
        setFolderImages(prev => prev.map(img => 
          img.path === imagePath ? { ...img, caption: captionText } : img
        ));
      }
    } catch (error) {
      console.error("Error saving caption:", error);
      toast.error(error.response?.data?.detail || "Failed to save caption");
    } finally {
      setSavingCaption(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingCaption(null);
    setCaptionText("");
  };

  // Count images with captions
  const captionedCount = folderImages.filter(img => img.caption && img.caption.trim()).length;

  // Bulk selection functions
  const toggleSelectionMode = () => {
    if (selectionMode) {
      setSelectedImages([]);
    }
    setSelectionMode(!selectionMode);
  };

  const toggleImageSelection = (imagePath) => {
    setSelectedImages(prev => {
      if (prev.includes(imagePath)) {
        return prev.filter(p => p !== imagePath);
      } else {
        return [...prev, imagePath];
      }
    });
  };

  const selectAllImages = () => {
    if (selectedImages.length === folderImages.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages(folderImages.map(img => img.path));
    }
  };

  const handleDuplicate = async () => {
    if (selectedImages.length === 0 || !selectedFolder) return;
    setDuplicating(true);
    try {
      const imagePaths = selectedImages.map(p => p.replace('/api/images/', '/var/www/thunderbuddy/backend/storage/images/'));
      const res = await axios.post(`${API}/folders/duplicate-images`, { image_paths: imagePaths });
      toast.success(`Duplicated ${res.data.duplicated} image(s)`);
      setSelectedImages([]);
      fetchFolderImages(selectedFolder);
    } catch { toast.error("Failed to duplicate"); }
    finally { setDuplicating(false); }
  };
  const handleBulkDelete = async () => {
    if (selectedImages.length === 0 || !selectedFolder) return;
    
    setBulkDeleting(true);
    let successCount = 0;
    let failCount = 0;
    
    try {
      for (const imagePath of selectedImages) {
        try {
          // Extract the relative path from the full API path
          const relativePath = imagePath.replace('/api/images/', '');
          await axios.delete(`${API}/images/delete/${encodeURIComponent(relativePath)}`);
          successCount++;
        } catch (error) {
          console.error(`Failed to delete ${imagePath}:`, error);
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`Deleted ${successCount} image${successCount > 1 ? 's' : ''}`);
      }
      if (failCount > 0) {
        toast.error(`Failed to delete ${failCount} image${failCount > 1 ? 's' : ''}`);
      }
      
      // Refresh the folder
      await fetchFolderImages(selectedFolder);
      await fetchFolders();
      setSelectedImages([]);
      setSelectionMode(false);
    } catch (error) {
      console.error("Bulk delete error:", error);
      toast.error("Failed to delete images");
    } finally {
      setBulkDeleting(false);
      setBulkDeleteDialogOpen(false);
    }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedFolder) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/heic", "image/heif", "image/bmp", "image/tiff"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please select a valid image file (JPG, PNG, GIF, WEBP)");
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${API}/images/folder/${encodeURIComponent(selectedFolder.path)}/upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.success) {
        toast.success("Image uploaded successfully");
        fetchFolderImages(selectedFolder);
        fetchFolders();
        fetchStats();
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploadingFile(false);
      setUploadDialogOpen(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!imageToDelete) return;

    try {
      await axios.delete(`${API}/images/delete/${encodeURIComponent(imageToDelete.relative_path)}`);
      toast.success("Image moved to trash (auto-deletes in 24 hours)");
      setDeleteDialogOpen(false);
      setImageToDelete(null);
      if (selectedFolder) {
        fetchFolderImages(selectedFolder);
      }
      fetchFolders();
      fetchStats();
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Failed to delete image");
    }
  };

  const toggleDateExpanded = (date) => {
    setExpandedDates((prev) => ({
      ...prev,
      [date]: !prev[date],
    }));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Filter folders based on search (phone, order ID, or date)
  const filteredFolders = folders.filter((folder) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      folder.phone_number?.toLowerCase().includes(query) ||
      folder.session_id?.toLowerCase().includes(query) ||
      folder.order_id?.toLowerCase().includes(query) ||
      folder.customer_name?.toLowerCase().includes(query) ||
      folder.date?.includes(query)
    );
  });

  // Group folders by date
  const groupedFolders = filteredFolders.reduce((acc, folder) => {
    const date = folder.date || "Unknown";
    if (!acc[date]) acc[date] = [];
    acc[date].push(folder);
    return acc;
  }, {});

  // Sort dates descending
  const sortedDates = Object.keys(groupedFolders).sort((a, b) => b.localeCompare(a));

  return (
    <div className="flex h-screen overflow-hidden" data-testid="image-dashboard">
      {/* Sidebar */}
      <Sidebar stats={stats} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="glass-header px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Image Gallery</h1>
            <p className="text-sm text-muted-foreground">
              Manage and view all customer images
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => { fetchFolders(); fetchStats(); }}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </header>

        {/* Stats Cards */}
        <div className="px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="shadow-soft">
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{stats.total_images || 0}</div>
              <div className="text-xs text-muted-foreground">Total Images</div>
            </CardContent>
          </Card>
          <Card className="shadow-soft bg-blue-50">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-700">{stats.total_folders || 0}</div>
              <div className="text-xs text-muted-foreground">Customer Folders</div>
            </CardContent>
          </Card>
          <Card className="shadow-soft bg-emerald-50">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-emerald-700">{stats.today_images || 0}</div>
              <div className="text-xs text-muted-foreground">Today&apos;s Images</div>
            </CardContent>
          </Card>
          <Card className="shadow-soft bg-purple-50">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-purple-700">{formatFileSize(stats.total_size)}</div>
              <div className="text-xs text-muted-foreground">Total Size</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 px-6 pb-6 overflow-hidden flex gap-4">
          {/* Folder List */}
          <Card className="w-80 flex flex-col shadow-soft">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <FolderOpen className="h-4 w-4" />
                  Folders
                </CardTitle>
              </div>
              {/* Search */}
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search phone / Order ID..."
                  className="pl-9 h-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-folders"
                />
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden p-0">
              <ScrollArea className="h-full">
                {loading ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredFolders.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center px-4">
                    <FolderOpen className="h-12 w-12 text-muted-foreground/30 mb-3" />
                    <p className="text-sm text-muted-foreground">No folders found</p>
                  </div>
                ) : (
                  <div className="p-2 space-y-3">
                    {sortedDates.map((date) => (
                      <div key={date}>
                        {/* Date Header */}
                        <button
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                          onClick={() => toggleDateExpanded(date)}
                        >
                          {expandedDates[date] !== false ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                          <Calendar className="h-4 w-4 text-primary" />
                          <span>{date}</span>
                          <Badge className="ml-auto bg-slate-200 text-slate-700 hover:bg-slate-200">
                            {groupedFolders[date].length}
                          </Badge>
                        </button>

                        {/* Folders under this date */}
                        {expandedDates[date] !== false && (
                          <div className="ml-2 mt-1 space-y-2">
                            {groupedFolders[date].map((folder) => (
                              <button
                                key={folder.path}
                                className={`w-full text-left p-3 rounded-xl transition-all ${
                                  selectedFolder?.path === folder.path
                                    ? "bg-[#5990DC]/10 border-2 border-[#5990DC] shadow-md"
                                    : "bg-white border border-slate-200 hover:border-[#5990DC]/50 hover:shadow-md"
                                }`}
                                onClick={() => fetchFolderImages(folder)}
                                data-testid={`folder-${folder.phone_number}`}
                              >
                                {/* Phone Number & Session */}
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <div className="p-1.5 rounded-lg bg-[#5990DC]/10">
                                      <Phone className="h-3.5 w-3.5 text-[#5990DC]" />
                                    </div>
                                    <span className="font-mono text-sm font-bold text-slate-800">
                                      {folder.phone_number}
                                    </span>
                                  </div>
                                  <Badge variant="outline" className="text-[10px] h-5 bg-slate-50 text-slate-600 font-mono">
                                    {folder.session_id}
                                  </Badge>
                                </div>
                                
                                {/* Order ID */}
                                <div className={`px-2 py-1 rounded-lg mb-2 ${
                                  folder.order_id === "Not Available" 
                                    ? "bg-orange-50" 
                                    : "bg-emerald-50"
                                }`}>
                                  <span className={`text-sm font-bold font-mono ${
                                    folder.order_id === "Not Available" 
                                      ? "text-orange-600" 
                                      : "text-emerald-700"
                                  }`}>
                                    {folder.order_id === "Not Available" ? "No Order" : folder.order_id}
                                  </span>
                                </div>
                                
                                {/* Product Details & Image Count - SINGLE LINE */}
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-xs font-semibold text-slate-600 bg-[#E8EDD4] px-2 py-1 rounded-lg truncate flex-1">
                                    {folder.product_details && folder.product_details !== "Not Available" 
                                      ? folder.product_details 
                                      : "No Product"}
                                  </span>
                                  <span className="text-xs font-bold text-[#5990DC] bg-[#5990DC]/10 px-2 py-1 rounded-lg whitespace-nowrap">
                                    {folder.image_count} img{folder.doc_count > 0 && ` • ${folder.doc_count} doc`}
                                  </span>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Image Grid with Drag & Drop */}
          <Card 
            className={`flex-1 flex flex-col shadow-soft relative transition-all ${
              isDragging ? 'ring-4 ring-[#5990DC] ring-opacity-50 bg-[#5990DC]/5' : ''
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {/* Drag overlay */}
            {isDragging && (
              <div className="absolute inset-0 z-50 flex items-center justify-center bg-[#5990DC]/10 backdrop-blur-sm rounded-lg border-4 border-dashed border-[#5990DC]">
                <div className="text-center">
                  <Upload className="h-16 w-16 text-[#5990DC] mx-auto mb-4" />
                  <p className="text-xl font-bold text-[#5990DC]">Drop images here</p>
                  <p className="text-sm text-slate-500 mt-1">JPG, PNG, GIF, WEBP supported</p>
                </div>
              </div>
            )}
            
            <CardHeader className="pb-3 border-b">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  {selectedFolder ? (
                    <div>
                      {/* Header Row - Phone & Upload */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-xl bg-[#5990DC]">
                          <ImageIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h2 className="text-lg font-bold text-slate-800">
                            {selectedFolder.phone_number}
                          </h2>
                          <p className="text-xs text-slate-500">Session: {selectedFolder.session_id}</p>
                        </div>
                      </div>
                      
                      {/* Single Line - Order ID | Product | Image Count */}
                      <div className="flex items-center gap-3 flex-wrap">
                        {/* Order ID */}
                        <span className={`text-sm font-bold font-mono px-3 py-1.5 rounded-lg ${
                          selectedFolder.order_id === "Not Available" 
                            ? "bg-orange-50 text-orange-600" 
                            : "bg-emerald-50 text-emerald-700"
                        }`}>
                          {selectedFolder.order_id === "Not Available" ? "No Order" : selectedFolder.order_id}
                        </span>
                        
                        {/* Product Details */}
                        <span className="text-sm font-semibold text-slate-700 bg-[#E8EDD4] px-3 py-1.5 rounded-lg">
                          {selectedFolder.product_details && selectedFolder.product_details !== "Not Available" 
                            ? selectedFolder.product_details 
                            : "No Product"}
                        </span>
                        
                        {/* Image Count */}
                        <span className="text-sm font-bold text-[#5990DC] bg-[#5990DC]/10 px-3 py-1.5 rounded-lg">
                          {selectedFolder.image_count} Images
                        </span>
                        
                        {/* Captioned Count */}
                        {captionedCount > 0 && (
                          <span className="text-sm font-bold text-green-700 bg-green-100 px-3 py-1.5 rounded-lg flex items-center gap-1">
                            <MessageSquare className="h-3.5 w-3.5" />
                            {captionedCount} Captioned
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <CardTitle className="text-base flex items-center gap-2">
                      <ImageIcon className="h-4 w-4" />
                      Select a folder
                    </CardTitle>
                  )}
                </div>
                {selectedFolder && (
                  <div className="flex items-center gap-2 ml-4">
                    {/* Hidden file input for direct upload */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileInputChange}
                      accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,image/bmp,image/tiff,.heic,.heif,.bmp,.tiff"
                      multiple
                      className="hidden"
                    />
                    
                    {/* Bulk Selection Controls */}
                    {selectionMode ? (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={selectAllImages}
                          className="text-xs"
                        >
                          {selectedImages.length === folderImages.length ? (
                            <>
                              <CheckSquare className="h-4 w-4 mr-1" />
                              Deselect All
                            </>
                          ) : (
                            <>
                              <Square className="h-4 w-4 mr-1" />
                              Select All ({folderImages.length})
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleDuplicate}
                          disabled={selectedImages.length === 0 || duplicating}
                          className="text-xs text-green-600 border-green-300 bg-green-50 hover:bg-green-100"
                        >
                          {duplicating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Copy className="h-4 w-4 mr-1" />}
                          Duplicate ({selectedImages.length})
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setBulkDeleteDialogOpen(true)}
                          disabled={selectedImages.length === 0 || bulkDeleting}
                          className="text-xs"
                          data-testid="bulk-delete-btn"
                        >
                          {bulkDeleting ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4 mr-1" />
                          )}
                          Delete ({selectedImages.length})
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={toggleSelectionMode}
                          className="text-xs"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={toggleSelectionMode}
                          className="text-xs"
                          data-testid="select-mode-btn"
                        >
                          <CheckSquare className="h-4 w-4 mr-1" />
                          Select
                        </Button>
                        <Button
                          size="sm"
                          onClick={openFilePicker}
                          disabled={uploadingFile}
                          className="bg-[#5990DC] hover:bg-[#4a7bc4] text-white"
                          data-testid="upload-image-btn"
                        >
                          {uploadingFile ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4 mr-2" />
                          )}
                          {uploadingFile ? "Uploading..." : "Upload"}
                        </Button>
                      </>
                    )}
                    <div className="flex border rounded-md">
                      <Button
                        variant={viewMode === "grid" ? "secondary" : "ghost"}
                        size="sm"
                        className="rounded-r-none"
                        onClick={() => setViewMode("grid")}
                      >
                        <Grid className="h-4 w-4" />
                      </Button>
                      <Button
                        variant={viewMode === "list" ? "secondary" : "ghost"}
                        size="sm"
                        className="rounded-l-none"
                        onClick={() => setViewMode("list")}
                      >
                        <List className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden p-0">
              {!selectedFolder ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <FolderOpen className="h-16 w-16 text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">Select a folder from the left to view images</p>
                </div>
              ) : loadingImages ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : folderImages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <ImageIcon className="h-16 w-16 text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground font-medium">No images in this folder</p>
                  <p className="text-sm text-muted-foreground/70 mb-4">
                    Upload images to get started
                  </p>
                  <Button onClick={openFilePicker} className="bg-[#5990DC] hover:bg-[#4a7bc4]">
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Images
                  </Button>
                  <p className="text-sm text-slate-400 mt-3">or drag & drop images here</p>
                </div>
              ) : (
                <ScrollArea className="h-full p-4">
                  {/* Caption count header */}
                  {captionedCount > 0 && (
                    <div className="flex items-center gap-2 mb-4 px-1">
                      <Badge variant="secondary" className="bg-green-100 text-green-700">
                        <MessageSquare className="h-3 w-3 mr-1" />
                        {captionedCount} captioned
                      </Badge>
                      <span className="text-xs text-muted-foreground">of {folderImages.length} images</span>
                    </div>
                  )}
                  
                  {viewMode === "grid" ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
                      {folderImages.map((image, index) => {
                        const isSelected = selectedImages.includes(image.path);
                        return (
                        <div
                          key={image.path}
                          className={`group relative bg-muted rounded-lg overflow-hidden border-2 transition-all ${
                            isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-transparent hover:border-slate-300'
                          }`}
                          data-testid={`image-${index}`}
                        >
                          {/* Image - smaller thumbnail with aspect-square */}
                          <div 
                            className="aspect-square relative cursor-pointer"
                            onClick={() => {
                              if (selectionMode) {
                                toggleImageSelection(image.path);
                              } else {
                                openLightbox(index);
                              }
                            }}
                          >
                            <img
                              src={`${BACKEND_URL}${image.path}`}
                              alt={image.filename}
                              className={`w-full h-full object-cover transition-opacity ${isSelected ? 'opacity-80' : ''}`}
                              loading="lazy"
                            />
                            
                            {/* Selection Checkbox */}
                            {selectionMode && (
                              <div 
                                className="absolute top-1 left-1 z-20"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleImageSelection(image.path);
                                }}
                              >
                                <div className={`h-6 w-6 rounded flex items-center justify-center transition-all ${
                                  isSelected 
                                    ? 'bg-blue-500 text-white' 
                                    : 'bg-white/90 border-2 border-slate-300 hover:border-blue-400'
                                }`}>
                                  {isSelected && <Check className="h-4 w-4" />}
                                </div>
                              </div>
                            )}
                            
                            {/* Caption indicator badge */}
                            {image.caption && image.caption.trim() && !selectionMode && (
                              <div className="absolute top-1 right-1">
                                <Badge className="h-5 px-1 bg-green-500 text-white text-[10px]">
                                  <MessageSquare className="h-3 w-3" />
                                </Badge>
                              </div>
                            )}
                            
                            {/* Image number badge - only show when not in selection mode */}
                            {!selectionMode && (
                              <div className="absolute top-1 left-1">
                                <Badge variant="secondary" className="text-[9px] h-4 px-1 bg-black/70 text-white">
                                  #{index + 1}
                                </Badge>
                              </div>
                            )}

                            {/* Overlay with actions - hide in selection mode */}
                            {!selectionMode && (
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                                <Button
                                  variant="secondary"
                                  size="icon"
                                  className="h-7 w-7 bg-white/90 hover:bg-white"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openLightbox(index);
                                  }}
                                >
                                  <ZoomIn className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setImageToDelete(image);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            )}
                          </div>

                          {/* Caption Section - Compact like ImageGalleryPopup */}
                          <div className="p-1.5 bg-background border-t">
                            {editingCaption === image.path ? (
                              <div className="space-y-1">
                                <Input
                                  value={captionText}
                                  onChange={(e) => setCaptionText(e.target.value.slice(0, 100))}
                                  placeholder="Caption (max 100)"
                                  className="h-6 text-[10px] px-1.5"
                                  maxLength={100}
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => {
                                    e.stopPropagation();
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      handleSaveCaption(image.path);
                                    }
                                    if (e.key === 'Escape') {
                                      handleCancelEdit();
                                    }
                                  }}
                                />
                                <div className="flex items-center justify-between">
                                  <span className="text-[8px] text-muted-foreground">
                                    {captionText.length}/100
                                  </span>
                                  <div className="flex gap-0.5">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-5 w-5 p-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancelEdit();
                                      }}
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      className="h-5 w-5 p-0 bg-green-600 hover:bg-green-700"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSaveCaption(image.path);
                                      }}
                                      disabled={savingCaption}
                                    >
                                      {savingCaption ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                      ) : (
                                        <Check className="h-3 w-3" />
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div
                                className="cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5 min-h-[20px] flex items-center"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStartEditCaption(image);
                                }}
                              >
                                {image.caption && image.caption.trim() ? (
                                  <p className="text-[10px] line-clamp-2 text-foreground font-medium">{image.caption}</p>
                                ) : (
                                  <p className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                                    <MessageSquare className="h-2.5 w-2.5" />
                                    Add caption
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                      })}
                      
                      {/* Drag & Drop hint when images exist - hide in selection mode */}
                      {!selectionMode && (
                        <div 
                          className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 hover:border-[#5990DC] bg-slate-50 hover:bg-[#5990DC]/5 transition-all cursor-pointer aspect-square p-2"
                          onClick={openFilePicker}
                        >
                          <Upload className="h-6 w-6 text-slate-400 mb-1" />
                          <span className="text-[10px] text-slate-500 text-center">Add more</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {folderImages.map((image, index) => (
                        <div
                          key={image.path}
                          className="flex items-center gap-4 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                          data-testid={`image-row-${index}`}
                        >
                          <div className="relative">
                            <img
                              src={`${BACKEND_URL}${image.path}`}
                              alt={image.filename}
                              loading="lazy"
                              className="w-16 h-16 object-cover rounded-md cursor-pointer"
                              onClick={() => openLightbox(index)}
                            />
                            {image.caption && image.caption.trim() && (
                              <Badge className="absolute -top-1 -right-1 h-4 px-1 bg-green-500 text-white text-[9px]">
                                <MessageSquare className="h-2.5 w-2.5" />
                              </Badge>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-mono text-sm truncate">{image.filename}</p>
                            <p className="text-xs text-muted-foreground mb-1">
                              {formatFileSize(image.size)}
                            </p>
                            {/* Caption inline edit for list view */}
                            <div 
                              className="cursor-pointer hover:bg-muted rounded px-1 py-0.5"
                              onClick={() => handleStartEditCaption(image)}
                            >
                              {image.caption && image.caption.trim() ? (
                                <p className="text-xs text-foreground truncate">{image.caption}</p>
                              ) : (
                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                  <MessageSquare className="h-3 w-3" />
                                  Add caption
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openLightbox(index)}
                            >
                              <ZoomIn className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => {
                                setImageToDelete(image);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move to Trash</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{imageToDelete?.filename}&quot; will be moved to trash. You can restore it within 24 hours before it&apos;s permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteImage} className="bg-destructive text-destructive-foreground">
              Move to Trash
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Image Lightbox with Slider */}
      {lightboxOpen && folderImages[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
          data-testid="image-lightbox"
        >
          {/* Close Button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/20 z-10"
            onClick={closeLightbox}
          >
            <X className="h-6 w-6" />
          </Button>
          
          {/* Image Counter */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 rounded-full px-4 py-2 z-10">
            <span className="text-white text-sm font-medium">
              {lightboxIndex + 1} / {folderImages.length}
            </span>
          </div>
          
          {/* Previous Button */}
          {folderImages.length > 1 && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 top-1/2 -translate-y-1/2 h-14 w-14 text-white bg-black/40 hover:bg-black/60 rounded-full z-10"
              onClick={(e) => {
                e.stopPropagation();
                goToPrevImage();
              }}
            >
              <ChevronLeft className="h-8 w-8" />
            </Button>
          )}
          
          {/* Next Button */}
          {folderImages.length > 1 && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-1/2 -translate-y-1/2 h-14 w-14 text-white bg-black/40 hover:bg-black/60 rounded-full z-10"
              onClick={(e) => {
                e.stopPropagation();
                goToNextImage();
              }}
            >
              <ChevronRight className="h-8 w-8" />
            </Button>
          )}
          
          {/* Main Image */}
          <div 
            className="flex items-center justify-center max-h-[85vh] max-w-[85vw]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`${BACKEND_URL}${folderImages[lightboxIndex].path}`}
              alt={folderImages[lightboxIndex].filename}
              className="max-h-[85vh] max-w-[85vw] object-contain transition-transform"
              style={{ transform: `scale(${lightboxZoom})` }}
            />
          </div>
          
          {/* Bottom Info Bar */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 bg-black/60 rounded-xl px-5 py-3 max-w-[80vw]">
            {/* Caption if exists */}
            {folderImages[lightboxIndex].caption && folderImages[lightboxIndex].caption.trim() && (
              <div className="flex items-center gap-2 text-green-400 w-full justify-center">
                <MessageSquare className="h-4 w-4" />
                <span className="text-sm font-medium truncate max-w-[60vw]">{folderImages[lightboxIndex].caption}</span>
              </div>
            )}
            
            <div className="flex items-center gap-4">
              <span className="text-white text-sm font-mono">{folderImages[lightboxIndex].filename}</span>
              <span className="text-white/70 text-sm">{formatFileSize(folderImages[lightboxIndex].size)}</span>
              <div className="flex items-center gap-1 border-l border-white/20 pl-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-white hover:bg-white/20"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxZoom(z => Math.max(0.5, z - 0.25));
                  }}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-white text-xs w-12 text-center">{Math.round(lightboxZoom * 100)}%</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-white hover:bg-white/20"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxZoom(z => Math.min(3, z + 0.25));
                  }}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-400 hover:bg-red-500/20 hover:text-red-300"
                onClick={(e) => {
                  e.stopPropagation();
                  setImageToDelete(folderImages[lightboxIndex]);
                  setDeleteDialogOpen(true);
                  closeLightbox();
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Delete {selectedImages.length} Image{selectedImages.length > 1 ? 's' : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The selected images will be permanently deleted
              from the server.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete All
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
